/**
 * Cross-Field Validation Types & Engine
 *
 * Supports form-level validation rules that compare or link multiple fields:
 *   - MATCH:               Two fields must have the same value
 *   - COMPARE:             Field A must be >, <, >=, <= Field B
 *   - CONDITIONAL_REQUIRE: If Field A has value X, then Field B is required
 *   - UNIQUE_ACROSS:       Two or more fields must all have different values
 */

import type { BuilderField, BuilderValidationError } from './formBuilderTypes';

// ─── Types ───

export type CrossFieldRuleType = 'MATCH' | 'COMPARE' | 'CONDITIONAL_REQUIRE' | 'UNIQUE_ACROSS';
export type CompareOperator = '>' | '<' | '>=' | '<=' | '==' | '!=';

export interface CrossFieldRule {
  id: string;
  type: CrossFieldRuleType;
  enabled: boolean;
  /** Fields involved (for MATCH: 2, UNIQUE: 2+, COMPARE: 2, COND_REQ: 2) */
  fieldIds: string[];
  /** Operator for COMPARE rules */
  operator?: CompareOperator;
  /** Trigger value for CONDITIONAL_REQUIRE */
  triggerValue?: unknown;
  /** Custom error message */
  errorMessage?: string;
}

// ─── Design-Time Validation (Builder) ───

export function validateCrossFieldRules(
  rules: CrossFieldRule[],
  allFields: BuilderField[],
): BuilderValidationError[] {
  const errors: BuilderValidationError[] = [];
  const fieldMap = new Map(allFields.map((f) => [f.id, f]));

  for (const rule of rules) {
    if (!rule.enabled) continue;

    // Check that all referenced fields exist
    for (const fid of rule.fieldIds) {
      if (!fieldMap.has(fid)) {
        errors.push({
          type: 'CROSS_FIELD_MISSING_REF',
          message: `Cross-field rule references non-existent field "${fid}"`,
          fieldId: fid,
          ruleId: rule.id,
        });
      }
    }

    // Rule-specific validations
    switch (rule.type) {
      case 'MATCH':
        if (rule.fieldIds.length !== 2) {
          errors.push({
            type: 'CROSS_FIELD_INVALID',
            message: 'MATCH rule requires exactly 2 fields',
            ruleId: rule.id,
          });
        }
        break;

      case 'COMPARE':
        if (rule.fieldIds.length !== 2) {
          errors.push({
            type: 'CROSS_FIELD_INVALID',
            message: 'COMPARE rule requires exactly 2 fields',
            ruleId: rule.id,
          });
        }
        if (!rule.operator) {
          errors.push({
            type: 'CROSS_FIELD_INVALID',
            message: 'COMPARE rule requires an operator',
            ruleId: rule.id,
          });
        }
        break;

      case 'CONDITIONAL_REQUIRE':
        if (rule.fieldIds.length !== 2) {
          errors.push({
            type: 'CROSS_FIELD_INVALID',
            message: 'CONDITIONAL_REQUIRE rule requires exactly 2 fields (trigger + target)',
            ruleId: rule.id,
          });
        }
        break;

      case 'UNIQUE_ACROSS':
        if (rule.fieldIds.length < 2) {
          errors.push({
            type: 'CROSS_FIELD_INVALID',
            message: 'UNIQUE_ACROSS rule requires at least 2 fields',
            ruleId: rule.id,
          });
        }
        break;
    }
  }

  return errors;
}

// ─── Runtime Evaluation ───

export interface CrossFieldViolation {
  ruleId: string;
  ruleType: CrossFieldRuleType;
  message: string;
  fieldIds: string[];
}

export function evaluateCrossFieldRules(
  rules: CrossFieldRule[],
  values: Record<string, unknown>,
): CrossFieldViolation[] {
  const violations: CrossFieldViolation[] = [];

  for (const rule of rules) {
    if (!rule.enabled) continue;

    switch (rule.type) {
      case 'MATCH': {
        const [fA, fB] = rule.fieldIds;
        const vA = values[fA];
        const vB = values[fB];
        if (vA != null && vB != null && String(vA) !== String(vB)) {
          violations.push({
            ruleId: rule.id,
            ruleType: 'MATCH',
            message: rule.errorMessage || 'Fields must have the same value',
            fieldIds: rule.fieldIds,
          });
        }
        break;
      }

      case 'COMPARE': {
        const [fA, fB] = rule.fieldIds;
        const nA = Number(values[fA]);
        const nB = Number(values[fB]);
        if (!isNaN(nA) && !isNaN(nB)) {
          let failed = false;
          switch (rule.operator) {
            case '>':  failed = !(nA > nB);  break;
            case '<':  failed = !(nA < nB);  break;
            case '>=': failed = !(nA >= nB); break;
            case '<=': failed = !(nA <= nB); break;
            case '==': failed = !(nA === nB); break;
            case '!=': failed = !(nA !== nB); break;
          }
          if (failed) {
            violations.push({
              ruleId: rule.id,
              ruleType: 'COMPARE',
              message: rule.errorMessage || `Field comparison (${rule.operator}) failed`,
              fieldIds: rule.fieldIds,
            });
          }
        }
        break;
      }

      case 'CONDITIONAL_REQUIRE': {
        const [triggerId, targetId] = rule.fieldIds;
        const triggerVal = values[triggerId];
        const targetVal = values[targetId];
        const matches = rule.triggerValue != null
          ? String(triggerVal) === String(rule.triggerValue)
          : triggerVal != null && String(triggerVal).trim() !== '';
        if (matches && (targetVal == null || String(targetVal).trim() === '')) {
          violations.push({
            ruleId: rule.id,
            ruleType: 'CONDITIONAL_REQUIRE',
            message: rule.errorMessage || 'This field is required based on another field\'s value',
            fieldIds: rule.fieldIds,
          });
        }
        break;
      }

      case 'UNIQUE_ACROSS': {
        const vals = rule.fieldIds
          .map((id) => values[id])
          .filter((v) => v != null && String(v).trim() !== '')
          .map(String);
        if (vals.length !== new Set(vals).size) {
          violations.push({
            ruleId: rule.id,
            ruleType: 'UNIQUE_ACROSS',
            message: rule.errorMessage || 'Fields must have unique values',
            fieldIds: rule.fieldIds,
          });
        }
        break;
      }
    }
  }

  return violations;
}
